import { ReactComponent as ArrowUpIcon } from '@app/assets/arrow-up.svg';
import { ChampionEvents } from '@app/domains/champion/analytics/posthog/events';
import ChampionPlanPriceInfo from '@app/domains/champion/components/ChampionPlanPriceInfo';
import type { ChampionPlanProps } from '@app/domains/champion/types';
import { championActionState } from '@app/domains/champion/utils/championActionState';
import ActivateButton from '@app/domains/common/components/button/ActivateButton';
import CancelButton from '@app/domains/common/components/button/CancelButton';
import clsx from 'clsx';
import { type Ref, forwardRef } from 'react';

export const ChampionPlanDefaultButton = forwardRef(function ChampionPlanButtonOnly(
    {
        actionType,
        name,
        htmlDesc,
        advChapterCount,
        price,
        discountInfo,
        descLines,
        showTierInfo,
        disabled,
        onAction,
        onChangePaymentMethod,
        ...props
    }: ChampionPlanProps,
    ref: Ref<HTMLDivElement>
) {
    return (
        <div {...props} ref={ref}>
            <SubscribeButton
                price={price}
                discountInfo={discountInfo}
                actionType={actionType}
                disabled={disabled}
                onAction={onAction}
                onChangePaymentMethod={onChangePaymentMethod}
                withPrice={false}
            />
        </div>
    );
});

export const ChampionPlanWithPriceButton = forwardRef(function ChampionPlanWithPriceButton(
    {
        actionType,
        name,
        htmlDesc,
        advChapterCount,
        price,
        discountInfo,
        descLines,
        showTierInfo,
        disabled,
        onAction,
        onChangePaymentMethod,
        ...props
    }: ChampionPlanProps,
    ref: Ref<HTMLDivElement>
) {
    return (
        <div {...props} ref={ref}>
            <SubscribeButton
                price={price}
                discountInfo={discountInfo}
                actionType={actionType}
                disabled={disabled}
                onAction={onAction}
                onChangePaymentMethod={onChangePaymentMethod}
                withPrice={true}
            />
        </div>
    );
});

export const ChampionPlanChangePaymentMethodOnlyButton = forwardRef(function ChampionPlanButtonOnly(
    {
        actionType,
        name,
        htmlDesc,
        advChapterCount,
        price,
        discountInfo,
        descLines,
        showTierInfo,
        disabled,
        onAction,
        onChangePaymentMethod,
        ...props
    }: ChampionPlanProps,
    ref: Ref<HTMLDivElement>
) {
    const { activating, cancelling } = championActionState(actionType);

    return (
        <div {...props} ref={ref}>
            {!activating && actionType === 'unsubscribe' && (
                <div
                    className="flex cursor-pointer items-center gap-x-2 text-gray-desc hover:text-blue-600 dark:hover:text-blue-500"
                    onClick={onChangePaymentMethod}
                >
                    <div className="font-set-m10 text-right sm:font-set-m12">Change payment method</div>
                    <ArrowUpIcon className="h-10 w-10 rotate-90" />
                </div>
            )}
        </div>
    );
});

interface SubscribeButtonProps
    extends Pick<
        ChampionPlanProps,
        'price' | 'discountInfo' | 'actionType' | 'disabled' | 'onAction' | 'onChangePaymentMethod'
    > {
    withPrice: boolean;
}
const SubscribeButton = ({
    price,
    discountInfo,
    actionType,
    disabled,
    onAction,
    onChangePaymentMethod,
    withPrice,
}: SubscribeButtonProps) => {
    const { activating, cancelling } = championActionState(actionType);

    return (
        <>
            {activating ? (
                <ActivateButton
                    className={clsx('', {
                        'h-32 w-108': !withPrice,
                        'h-auto min-h-52 w-full max-w-335 !rounded-12': withPrice,
                    })}
                    disabled={disabled}
                    onClick={() => {
                        onAction?.(actionType);
                    }}
                    data-posthog-click-event={ChampionEvents.ClickChampionSubscribe}
                >
                    {disabled ? 'UNAVAILABLE' : actionType.toUpperCase()}
                    {withPrice && <PriceInfo price={price} discountInfo={discountInfo} />}
                </ActivateButton>
            ) : (
                <div className="flex flex-col items-center gap-y-4">
                    <CancelButton
                        className={clsx('', {
                            'h-32 w-108': !withPrice,
                            'h-auto min-h-52 w-full max-w-335 !rounded-12': withPrice,
                        })}
                        onClick={() => {
                            onAction?.(actionType);
                        }}
                    >
                        {actionType.toUpperCase()}
                    </CancelButton>
                    {actionType === 'unsubscribe' && (
                        <div
                            className="flex cursor-pointer items-center gap-x-2 text-gray-desc hover:text-blue-600 dark:hover:text-blue-500"
                            onClick={onChangePaymentMethod}
                        >
                            <div className="font-set-m10 text-right sm:font-set-m12">Change payment method</div>
                            <ArrowUpIcon className="h-10 w-10 rotate-90" />
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

type PriceInfoType = Pick<ChampionPlanProps, 'price' | 'discountInfo'> & {};

const PriceInfo = ({ price, discountInfo }: PriceInfoType) => {
    return (
        <span className="block font-semibold text-[12px] opacity-70">
            <ChampionPlanPriceInfo
                price={price}
                discountInfo={discountInfo}
                withDiscountDurationWarning={true}
                priceProps={{
                    className: '',
                }}
                discountedPriceProps={{
                    className: '',
                }}
                durationProps={{
                    className: '',
                }}
            />
        </span>
    );
};
